/**
 * ====================================================================
 * FIKO CAD DIMENSION GLOW EFFECT
 * ====================================================================
 *
 * WebGL shader tabanlı glow efekti
 * Selection, hover, drag durumları için görsel feedback
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { GlowEffectConfig } from './DimensionVisualStyle';
import { Dimension } from '../DimensionTypes';

// ====================================================================
// SHADER DEFINITIONS
// ====================================================================

/**
 * Glow vertex shader
 */
const GLOW_VERTEX_SHADER = `
varying vec2 vUv;
varying vec3 vPosition;

void main() {
    vUv = uv;
    vPosition = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

/**
 * Glow fragment shader
 */
const GLOW_FRAGMENT_SHADER = `
uniform vec3 glowColor;
uniform float glowIntensity;
uniform float glowRadius;
uniform float time;
uniform float pulseSpeed;
uniform float pulseAmount;

varying vec2 vUv;
varying vec3 vPosition;

void main() {
    // Merkeze olan mesafe
    float dist = length(vUv - 0.5) * 2.0;

    // Gaussian glow
    float glow = exp(-dist * dist / (glowRadius * glowRadius));

    // Pulse efekti (opsiyonel)
    float pulse = 1.0;
    if (pulseSpeed > 0.0) {
        pulse = 1.0 + sin(time * pulseSpeed) * pulseAmount;
    }

    // Final renk
    float alpha = glow * glowIntensity * pulse;

    // Kenar yumuşatma
    alpha *= smoothstep(1.0, 0.0, dist);

    gl_FragColor = vec4(glowColor, alpha);
}
`;

/**
 * Outline glow shader (kenar çizgisi için)
 */
const OUTLINE_GLOW_FRAGMENT_SHADER = `
uniform vec3 glowColor;
uniform float glowIntensity;
uniform float outlineWidth;
uniform float time;
uniform float pulseSpeed;

varying vec2 vUv;

void main() {
    // Kenar mesafesi
    float edgeDist = min(
        min(vUv.x, 1.0 - vUv.x),
        min(vUv.y, 1.0 - vUv.y)
    );

    // Outline glow
    float outline = 1.0 - smoothstep(0.0, outlineWidth, edgeDist);

    // Pulse
    float pulse = 1.0;
    if (pulseSpeed > 0.0) {
        pulse = 0.7 + 0.3 * sin(time * pulseSpeed);
    }

    float alpha = outline * glowIntensity * pulse;

    gl_FragColor = vec4(glowColor, alpha);
}
`;

// ====================================================================
// GLOW EFFECT CLASS
// ====================================================================

/**
 * Dimension Glow Effect - WebGL shader tabanlı glow
 */
export class DimensionGlowEffect {
    // Aktif glow'lar
    private static activeGlows = new Map<string, GlowInstance>();

    // Shared uniforms
    private static sharedUniforms = {
        time: { value: 0 }
    };

    // Animation frame
    private static animationFrame: number | null = null;

    /**
     * Animasyon döngüsünü başlat
     */
    private static startAnimationLoop(): void {
        if (this.animationFrame !== null) return;

        const animate = () => {
            this.sharedUniforms.time.value = performance.now() / 1000;

            // Tüm aktif glow'ların time uniform'unu güncelle
            this.activeGlows.forEach(glow => {
                if (glow.material.uniforms.time) {
                    glow.material.uniforms.time.value = this.sharedUniforms.time.value;
                }
            });

            if (this.activeGlows.size > 0) {
                this.animationFrame = requestAnimationFrame(animate);
            } else {
                this.animationFrame = null;
            }
        };

        this.animationFrame = requestAnimationFrame(animate);
    }

    /**
     * Glow shader materyali oluştur
     */
    static createGlowMaterial(config: GlowEffectConfig): THREE.ShaderMaterial {
        return new THREE.ShaderMaterial({
            uniforms: {
                glowColor: { value: new THREE.Color(config.color) },
                glowIntensity: { value: config.intensity },
                glowRadius: { value: config.radius },
                time: { value: 0 },
                pulseSpeed: { value: config.pulseSpeed || 0 },
                pulseAmount: { value: 0.2 }
            },
            vertexShader: GLOW_VERTEX_SHADER,
            fragmentShader: GLOW_FRAGMENT_SHADER,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            blending: config.blendMode === 'additive'
                ? THREE.AdditiveBlending
                : THREE.NormalBlending,
            side: THREE.DoubleSide
        });
    }

    /**
     * Outline glow materyali oluştur
     */
    static createOutlineGlowMaterial(config: GlowEffectConfig): THREE.ShaderMaterial {
        return new THREE.ShaderMaterial({
            uniforms: {
                glowColor: { value: new THREE.Color(config.color) },
                glowIntensity: { value: config.intensity },
                outlineWidth: { value: 0.1 },
                time: { value: 0 },
                pulseSpeed: { value: config.pulseSpeed || 0 }
            },
            vertexShader: GLOW_VERTEX_SHADER,
            fragmentShader: OUTLINE_GLOW_FRAGMENT_SHADER,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            blending: config.blendMode === 'additive'
                ? THREE.AdditiveBlending
                : THREE.NormalBlending,
            side: THREE.DoubleSide
        });
    }

    /**
     * Dimension'a glow ekle
     */
    static applyGlow(
        dimension: Dimension,
        config: GlowEffectConfig
    ): THREE.Mesh {
        const dimensionId = dimension.userData.dimensionId;

        // Mevcut glow varsa kaldır
        this.removeGlow(dimension);

        // Dimension'ın bounding box'ını al
        const box = new THREE.Box3().setFromObject(dimension);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        // Glow plane oluştur (biraz daha büyük)
        const glowSize = Math.max(size.x, size.y, size.z) * config.scale;
        const glowGeometry = new THREE.PlaneGeometry(glowSize, glowSize);

        const glowMaterial = this.createGlowMaterial(config);
        const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);

        // Pozisyon
        glowMesh.position.copy(center);

        // Billboard yapısı (her zaman kameraya baksın)
        glowMesh.userData.isBillboard = true;
        glowMesh.userData.glowConfig = config;

        // Render order (dimension'dan önce)
        glowMesh.renderOrder = (dimension.renderOrder || 999) - 1;

        // Aktif glow'lara ekle
        this.activeGlows.set(dimensionId, {
            mesh: glowMesh,
            material: glowMaterial,
            dimension
        });

        // Animasyon döngüsünü başlat
        this.startAnimationLoop();

        return glowMesh;
    }

    /**
     * Dimension'dan glow kaldır
     */
    static removeGlow(dimension: Dimension): void {
        const dimensionId = dimension.userData.dimensionId;
        const glowInstance = this.activeGlows.get(dimensionId);

        if (glowInstance) {
            // Mesh'i parent'tan kaldır
            if (glowInstance.mesh.parent) {
                glowInstance.mesh.parent.remove(glowInstance.mesh);
            }

            // Dispose
            glowInstance.mesh.geometry.dispose();
            glowInstance.material.dispose();

            // Map'ten kaldır
            this.activeGlows.delete(dimensionId);
        }
    }

    /**
     * Glow animasyonu - intensity geçişi
     */
    static animateGlowIntensity(
        dimension: Dimension,
        fromIntensity: number,
        toIntensity: number,
        duration: number,
        easing: (t: number) => number = t => t,
        onComplete?: () => void
    ): void {
        const dimensionId = dimension.userData.dimensionId;
        const glowInstance = this.activeGlows.get(dimensionId);

        if (!glowInstance) return;

        const startTime = performance.now();

        const animate = () => {
            const elapsed = performance.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const easedProgress = easing(progress);

            glowInstance.material.uniforms.glowIntensity.value =
                fromIntensity + (toIntensity - fromIntensity) * easedProgress;

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                onComplete?.();
            }
        };

        requestAnimationFrame(animate);
    }

    /**
     * Glow rengini değiştir
     */
    static setGlowColor(dimension: Dimension, color: number): void {
        const dimensionId = dimension.userData.dimensionId;
        const glowInstance = this.activeGlows.get(dimensionId);

        if (glowInstance) {
            glowInstance.material.uniforms.glowColor.value.setHex(color);
        }
    }

    /**
     * Glow intensity'yi değiştir
     */
    static setGlowIntensity(dimension: Dimension, intensity: number): void {
        const dimensionId = dimension.userData.dimensionId;
        const glowInstance = this.activeGlows.get(dimensionId);

        if (glowInstance) {
            glowInstance.material.uniforms.glowIntensity.value = intensity;
        }
    }

    /**
     * Pulse'ı etkinleştir/devre dışı bırak
     */
    static setPulse(dimension: Dimension, enabled: boolean, speed: number = 3): void {
        const dimensionId = dimension.userData.dimensionId;
        const glowInstance = this.activeGlows.get(dimensionId);

        if (glowInstance) {
            glowInstance.material.uniforms.pulseSpeed.value = enabled ? speed : 0;
        }
    }

    /**
     * Tüm glow'ları güncelle (kamera için billboard)
     */
    static updateBillboards(camera: THREE.Camera): void {
        this.activeGlows.forEach(glowInstance => {
            if (glowInstance.mesh.userData.isBillboard) {
                glowInstance.mesh.quaternion.copy(camera.quaternion);
            }
        });
    }

    /**
     * Tüm glow'ları temizle
     */
    static clearAll(): void {
        this.activeGlows.forEach((_, dimensionId) => {
            const glowInstance = this.activeGlows.get(dimensionId);
            if (glowInstance) {
                if (glowInstance.mesh.parent) {
                    glowInstance.mesh.parent.remove(glowInstance.mesh);
                }
                glowInstance.mesh.geometry.dispose();
                glowInstance.material.dispose();
            }
        });

        this.activeGlows.clear();

        if (this.animationFrame !== null) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
    }

    /**
     * Hover durumu için glow
     */
    static applyHoverGlow(dimension: Dimension, config: GlowEffectConfig): THREE.Mesh {
        const hoverConfig: GlowEffectConfig = {
            ...config,
            intensity: config.intensity * 0.6,
            pulseSpeed: 0
        };
        return this.applyGlow(dimension, hoverConfig);
    }

    /**
     * Selection durumu için glow
     */
    static applySelectionGlow(dimension: Dimension, config: GlowEffectConfig): THREE.Mesh {
        const selectionConfig: GlowEffectConfig = {
            ...config,
            intensity: config.intensity,
            pulseSpeed: 2
        };
        return this.applyGlow(dimension, selectionConfig);
    }

    /**
     * Drag durumu için glow
     */
    static applyDragGlow(dimension: Dimension, config: GlowEffectConfig): THREE.Mesh {
        const dragConfig: GlowEffectConfig = {
            ...config,
            color: 0x000000, // 🎯 SİYAH
            intensity: config.intensity * 1.2,
            pulseSpeed: 4
        };
        return this.applyGlow(dimension, dragConfig);
    }

    /**
     * Glow instance kontrolü
     */
    static hasGlow(dimension: Dimension): boolean {
        return this.activeGlows.has(dimension.userData.dimensionId);
    }

    /**
     * Aktif glow sayısı
     */
    static getActiveGlowCount(): number {
        return this.activeGlows.size;
    }

    // ====================================================================
    // INSTANCE METHODS (DimensionManager uyumu için)
    // ====================================================================

    /**
     * Instance: Objeye glow uygula
     * @param object THREE.Object3D veya Dimension
     * @param intensityOrConfig Intensity sayısı veya GlowEffectConfig
     */
    applyToObject(object: THREE.Object3D, intensityOrConfig?: number | GlowEffectConfig): THREE.Mesh | null {
        if (object && object.userData?.dimensionId) {
            // Config oluştur
            let config: GlowEffectConfig;

            if (typeof intensityOrConfig === 'number') {
                // Sadece intensity verilmiş, varsayılan config kullan
                config = {
                    enabled: true,
                    intensity: intensityOrConfig,
                    color: 0xFFD700,
                    radius: 0.5,
                    scale: 1.5,
                    blendMode: 'additive' as const
                };
            } else if (intensityOrConfig) {
                config = intensityOrConfig;
            } else {
                // Varsayılan config
                config = {
                    enabled: true,
                    intensity: 0.6,
                    color: 0xFFD700,
                    radius: 0.5,
                    scale: 1.5,
                    blendMode: 'additive' as const
                };
            }

            return DimensionGlowEffect.applyGlow(object as Dimension, config);
        }
        return null;
    }

    /**
     * Instance: Objeden glow kaldır
     */
    removeFromObject(object: THREE.Object3D): void {
        if (object && object.userData?.dimensionId) {
            DimensionGlowEffect.removeGlow(object as Dimension);
        }
    }

    /**
     * Instance: Dispose (temizlik)
     */
    dispose(): void {
        DimensionGlowEffect.clearAll();
    }
}

// ====================================================================
// TYPES
// ====================================================================

interface GlowInstance {
    mesh: THREE.Mesh;
    material: THREE.ShaderMaterial;
    dimension: Dimension;
}

// ====================================================================
// EXPORTS
// ====================================================================

export type { GlowEffectConfig };
